import React, {
  type ReactNode,
  useCallback,
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
} from 'react';
import {
  TagGroup as TagGroupRAC,
  TagList as TagListRAC,
  Tag as TagRAC,
  TextField as TextFieldRAC,
  Input as InputRAC,
  type Selection,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { findChildOfType, applyInternalProps } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';
import { FieldsetHeadless } from '../fieldset/fieldset-headless';

import { type TagsFieldProps, type TagsFieldOptions } from './tags-field-props';
import { getOtherValue, OTHER_ID } from './tags-field-utils';

export type TagsProps = {
  children: ReactNode;
};

type OptionLabelProps = {
  className?: string;
};

type OptionInternalProps = {
  value: string;
  label: string;
  isOther?: boolean;
  isFirstOption?: boolean;
};

const OptionLabel = ({ className, ...internal }: OptionLabelProps) => {
  const { label } = internal as OptionInternalProps;
  return <span className={className}>{label}</span>;
};

type OtherInputProps = {
  className?: string;
};

const OtherInput = ({ className }: OtherInputProps) => {
  const {
    value: fieldValue,
    onChange,
    addOtherLabel,
    addOtherPlaceholder,
    otherInputCharacterLimit,
  } = useFieldPropsV2<TagsFieldProps>();
  const { t } = useTranslation();

  const otherLabel =
    addOtherLabel || t('add-other.default-other-option-label') || OTHER_ID;

  const otherValue = useMemo(() => {
    return getOtherValue(fieldValue || [], otherLabel);
  }, [fieldValue, otherLabel]);

  const [inputValue, setInputValue] = useState(otherValue);

  const onChangeRef = useRef(onChange);

  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  });

  const handleInputChange = useCallback(
    (newValue: string) => {
      setInputValue(newValue);

      const regularOptions = (fieldValue || []).filter(
        (val) => !val.startsWith(`${otherLabel}:`) && val !== otherLabel,
      );

      const formattedOtherValue = newValue
        ? `${otherLabel}: ${newValue}`
        : otherLabel;

      onChangeRef.current([...regularOptions, formattedOtherValue]);
    },
    [fieldValue, otherLabel],
  );

  return (
    <TextFieldRAC
      value={inputValue}
      onChange={handleInputChange}
      aria-label={otherLabel}
    >
      <InputRAC
        placeholder={addOtherPlaceholder}
        maxLength={otherInputCharacterLimit}
        className={className}
      />
    </TextFieldRAC>
  );
};

type OptionProps = {
  children: ReactNode;
  className?: string;
};

const Option = ({ children, className, ...internal }: OptionProps) => {
  const { value, label, isOther, isFirstOption } =
    internal as OptionInternalProps;
  const { id } = useFieldPropsV2<TagsFieldProps>();

  const ref = useRef<HTMLDivElement>(null);
  useFocusFieldEvent(() => {
    if (isFirstOption) {
      ref.current?.focus();
    }
  }, id);

  const labelElement = findChildOfType(children, OptionLabel);

  return (
    <TagRAC ref={ref} id={value} textValue={label} className={className}>
      {applyInternalProps<OptionInternalProps>(labelElement, {
        value,
        label,
        isOther,
        isFirstOption,
      })}
    </TagRAC>
  );
};

type OptionsProps = {
  children: ReactNode;
  className?: string;
};

const Options = ({ children, className }: OptionsProps) => {
  const { options, addOther, addOtherLabel } =
    useFieldPropsV2<TagsFieldProps>();
  const { ariaDescribedBy } = useFieldAttributes();
  const { t } = useTranslation();

  const otherLabel =
    addOtherLabel || t('add-other.default-other-option-label') || OTHER_ID;

  const optionElement = findChildOfType(children, Option);

  const allOptions: (TagsFieldOptions & { isOther?: boolean })[] =
    useMemo(() => {
      const regularOptions = options || [];
      if (addOther) {
        return [
          ...regularOptions,
          {
            label: otherLabel,
            value: otherLabel,
            isOther: true,
          },
        ];
      }
      return regularOptions;
    }, [options, addOther, otherLabel]);

  return (
    <TagListRAC aria-describedby={ariaDescribedBy} className={className}>
      {allOptions.map((option, index) => {
        const optionValue = option.value ?? option.label;
        return applyInternalProps<OptionInternalProps>(optionElement, {
          key: optionValue,
          value: optionValue,
          label: option.label,
          isOther: option.isOther,
          isFirstOption: index === 0,
        });
      })}
    </TagListRAC>
  );
};

type ControlProps = {
  children: ReactNode;
  className?: string;
};

const Control = ({ children, className }: ControlProps) => {
  const {
    addOther,
    addOtherLabel,
    hasError,
    value: fieldValue,
  } = useFieldPropsV2<TagsFieldProps>();
  const { t } = useTranslation();

  const otherLabel =
    addOtherLabel || t('add-other.default-other-option-label') || OTHER_ID;

  const optionsElement = findChildOfType(children, Options);
  const otherInputElement = findChildOfType(children, OtherInput);

  const isOtherSelected = useMemo(() => {
    if (!addOther || !fieldValue) {
      return false;
    }
    return fieldValue.some(
      (val) => val === otherLabel || val.startsWith(`${otherLabel}:`),
    );
  }, [addOther, fieldValue, otherLabel]);

  return (
    <div className={className} data-invalid={hasError}>
      {optionsElement}
      {isOtherSelected && otherInputElement}
    </div>
  );
};

export const Tags = ({ children }: TagsProps) => {
  const {
    id,
    value,
    onChange,
    addOtherLabel,
    FieldLayout,
    required,
    onBlur,
    onFocus,
  } = useFieldPropsV2<TagsFieldProps>();
  const { inputId, labelId, ariaDescribedBy } = useFieldAttributes();
  const { t } = useTranslation();

  const otherLabel =
    addOtherLabel || t('add-other.default-other-option-label') || OTHER_ID;

  const handleSelectionChange = useCallback(
    (selection: Selection) => {
      if (selection === 'all') {
        return;
      }
      const selectedArray = Array.from(selection) as string[];

      const hasOther = selectedArray.includes(otherLabel);

      const regularValues = selectedArray.filter((v) => v !== otherLabel);

      if (hasOther) {
        const existingOtherValue = (value || []).find(
          (val) => val === otherLabel || val.startsWith(`${otherLabel}:`),
        );
        onChange([...regularValues, existingOtherValue || otherLabel]);
      } else {
        onChange(regularValues);
      }
    },
    [onChange, value, otherLabel],
  );

  const selectedKeys = useMemo(() => {
    const normalizedValues = (value || []).map((val) => {
      if (val === otherLabel || val.startsWith(`${otherLabel}:`)) {
        return otherLabel;
      }
      return val;
    });
    return new Set(normalizedValues);
  }, [value, otherLabel]);

  const labelElement = findChildOfType(children, Label);
  const controlElement = findChildOfType(children, Control);
  const descriptionElement = findChildOfType(children, Description);
  const errorElement = findChildOfType(children, Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() => (
        <FieldsetHeadless
          id={inputId}
          ariaLabelledBy={labelId}
          ariaDescribedBy={ariaDescribedBy}
          required={required}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          <TagGroupRAC
            selectionMode="multiple"
            selectedKeys={selectedKeys}
            onSelectionChange={handleSelectionChange}
          >
            {controlElement}
          </TagGroupRAC>
        </FieldsetHeadless>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

Options.Option = Option;
Option.Label = OptionLabel;

Tags.Label = Label;
Tags.Control = Control;
Tags.Options = Options;
Tags.OtherInput = OtherInput;
Tags.Description = Description;
Tags.Error = Error;
